import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { EVENT_NAME, ReconciliationStatus } from 'common/constants';
import { Dialog } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { getCompanyId } from 'components/StoreSelect/slice';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { useVoidReconciliationPayment } from 'hooks/useClientDataServices';
import { ReconciliationReportDataType } from 'pages/ReconciliationReportPage/types';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { formatLocaleDate } from 'utils/dateUtils';
import { labelMessages } from './messages';
import { StyledIconDeclined } from './styled';
import { VoidPaymentDialogContent } from './VoidPaymentDialogContent';

export const VoidPaymentDialog = ({
  data: { status, documentId, vendorId, vendorName, paymentAmount, paymentDate }
}: {
  data: ReconciliationReportDataType;
}): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState<boolean>(false);
  const companyId = useAppSelector(getCompanyId);
  const intl = useIntl();
  const amplitude = useAmplitude();
  const { data, error, isLoading, mutate } = useVoidReconciliationPayment();

  const voidConfirmation = () => {
    setIsVoidDialogOpen(true);
  };

  const handleVoidDialogClose = () => {
    setIsVoidDialogOpen(false);
  };

  const handleVoidPayment = useCallback(() => {
    if (documentId) {
      mutate({ companyId, documentId });
      amplitudeTracker(amplitude, EVENT_NAME.VoidVirtualCard);
    }
  }, [amplitude, companyId, documentId, mutate]);

  const buttonsForDialog = [
    {
      id: 'void',
      onClick: handleVoidPayment,
      text: intl.formatMessage(labelMessages.voidButton),
      isLoading,
      variant: 'text'
    }
  ];

  const successToastMessage = `${intl.formatMessage(
    labelMessages.documentIdInDialogLabel
  )} #${documentId} ${intl.formatMessage(
    labelMessages.voidPaymentSuccessfulMessage
  )}`;

  useEffect(() => {
    if (!isLoading) {
      setIsVoidDialogOpen(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (data?.success) {
      dispatch(setSuccessMessage(successToastMessage));
      const refetchingData = async () => {
        await queryClient.invalidateQueries(['reconciliationReport']);
      };
      refetchingData().catch(() => {
        dispatch(
          setErrorMessages([
            intl.formatMessage(labelMessages.refetchErrorMessage)
          ])
        );
      });
    } else if (data?.failureErrorMessage) {
      dispatch(setErrorMessages([data.failureErrorMessage]));
    } else if (data?.errors?.length) {
      dispatch(setErrorMessages(data.errors));
    } else if (error) {
      dispatch(
        setErrorMessages([
          intl.formatMessage(labelMessages.voidPaymentErrorMessage)
        ])
      );
    }
  }, [
    data,
    dispatch,
    error,
    intl,
    isLoading,
    queryClient,
    successToastMessage
  ]);

  return (
    <>
      <Dialog
        buttonsProps={buttonsForDialog}
        handleClose={handleVoidDialogClose}
        title={intl.formatMessage(labelMessages.voidPaymentDialogTitle)}
        open={isVoidDialogOpen}
        dataTestId="void-payment-dialog"
        id="void-payment-dialog"
      >
        <VoidPaymentDialogContent
          documentId={documentId}
          vendorName={vendorName}
          vendorId={vendorId}
          paymentDate={formatLocaleDate(paymentDate, intl.locale)}
          paymentAmount={paymentAmount}
        />
      </Dialog>
      {(status === ReconciliationStatus.Pending ||
        status === ReconciliationStatus.Expired) && (
        <StyledIconDeclined
          onClick={voidConfirmation}
          data-testid="icon-declined"
        />
      )}
    </>
  );
};
